import { AuthProvider, AuthTokens, UserInfo, LoginOptions } from '../types';
import { tokenStorage } from '../TokenStorage';
import { apiInterceptor } from '../ApiInterceptor';

export interface KeycloakConfig {
  url: string;
  realm: string;
  clientId: string;
  redirectUri: string;
}

export class KeycloakProvider implements AuthProvider {
  private static readonly CODE_VERIFIER_KEY = 'keycloak_code_verifier';
  private config: KeycloakConfig;
  private authWindow: Window | null = null;

  constructor(config: KeycloakConfig) {
    this.config = config;
  }

  getProviderName(): string {
    return 'Keycloak';
  }

  isConfigured(): boolean {
    return !!(this.config.url && this.config.realm && this.config.clientId);
  }

  async login(options?: LoginOptions): Promise<void> {
    if (!this.isConfigured()) {
      throw new Error('Keycloak provider is not properly configured');
    }

    const authUrl = await this.buildAuthUrl(options);
    
    // For popup-based login (can be changed to redirect if preferred)
    return new Promise((resolve, reject) => {
      this.authWindow = window.open(
        authUrl,
        'keycloak-login',
        'width=500,height=600,scrollbars=yes,resizable=yes'
      );

      if (!this.authWindow) {
        reject(new Error('Failed to open authentication popup'));
        return;
      }

      // Check if popup was closed manually
      const checkClosed = setInterval(() => {
        if (this.authWindow?.closed) {
          clearInterval(checkClosed);
          reject(new Error('Authentication window was closed'));
        }
      }, 1000);

      // Listen for the callback message
      const messageHandler = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) {
          return;
        }

        if (event.data.type === 'KEYCLOAK_AUTH_SUCCESS') {
          clearInterval(checkClosed);
          window.removeEventListener('message', messageHandler);
          this.authWindow?.close();
          resolve();
        } else if (event.data.type === 'KEYCLOAK_AUTH_ERROR') {
          clearInterval(checkClosed);
          window.removeEventListener('message', messageHandler);
          this.authWindow?.close();
          reject(new Error(event.data.error || 'Authentication failed'));
        }
      };

      window.addEventListener('message', messageHandler);
    });
  }

  async logout(): Promise<void> {
    const tokens = tokenStorage.getTokens();
    
    // Clear local tokens first
    tokenStorage.clearTokens();

    // If we have tokens, perform server-side logout
    if (tokens) {
      try {
        const logoutUrl = `${this.config.url}/realms/${this.config.realm}/protocol/openid-connect/logout`;
        
        // Use hidden iframe for logout to avoid popup
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = `${logoutUrl}?post_logout_redirect_uri=${encodeURIComponent(window.location.origin)}`;
        document.body.appendChild(iframe);
        
        // Clean up iframe after a short delay
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      } catch (error) {
        logger.error('Server-side logout failed:', error);
        // Continue with local logout even if server logout fails
      }
    }
  }

  async handleCallback(): Promise<AuthTokens> {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');
    const state = urlParams.get('state');

    if (error) {
      throw new Error(errorDescription || error);
    }

    if (!code) {
      throw new Error('No authorization code received');
    }

    // Exchange code for tokens
    const tokens = await this.exchangeCodeForTokens(code);
    
    // Store tokens
    tokenStorage.setTokens(tokens);

    // Clean up code verifier from sessionStorage for security
    sessionStorage.removeItem(KeycloakProvider.CODE_VERIFIER_KEY);

    // Notify parent window if this is a popup
    if (window.opener) {
      window.opener.postMessage({ type: 'KEYCLOAK_AUTH_SUCCESS' }, window.location.origin);
    }

    return tokens;
  }

  async getTokens(): Promise<AuthTokens | null> {
    return tokenStorage.getTokens();
  }

  async refreshTokens(): Promise<AuthTokens> {
    const currentTokens = tokenStorage.getTokens();
    
    if (!currentTokens?.refreshToken) {
      throw new Error('No refresh token available');
    }

    const tokenUrl = `${this.config.url}/realms/${this.config.realm}/protocol/openid-connect/token`;
    
    const params = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: this.config.clientId,
      refresh_token: currentTokens.refreshToken,
    });

    // Use original fetch to bypass auth interceptor (prevents infinite loop)
    const originalFetch = apiInterceptor.getOriginalFetch();
    
    const response = await originalFetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error_description || 'Failed to refresh tokens');
    }

    const tokenData = await response.json();
    
    const newTokens = this.parseTokenResponse(tokenData);
    
    // Store updated tokens
    tokenStorage.setTokens(newTokens);
    
    return newTokens;
  }

  isTokenValid(tokens: AuthTokens): boolean {
    return !tokenStorage.isTokenExpired(tokens);
  }

  async getUserInfo(tokens: AuthTokens): Promise<UserInfo> {
    const userInfoUrl = `${this.config.url}/realms/${this.config.realm}/protocol/openid-connect/userinfo`;

    const response = await fetch(userInfoUrl, {
      headers: {
        'Authorization': `Bearer ${tokens.accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get user info: ${response.status}`);
    }

    const userData = await response.json();
    
    // Extract roles from token or user info
    const realmRoles: string[] = [];
    const clientRoles: string[] = [];
    
    // Try to extract roles from access token
    try {
      const tokenPayload = JSON.parse(atob(tokens.accessToken.split('.')[1]));
      
      // Realm roles
      if (tokenPayload.realm_access?.roles) {
        realmRoles.push(...tokenPayload.realm_access.roles.filter((role: string) => role !== 'default-roles-' + this.config.realm));
      }
      
      // Client roles
      if (tokenPayload.resource_access?.[this.config.clientId]?.roles) {
        clientRoles.push(...tokenPayload.resource_access[this.config.clientId].roles);
      }
    } catch (error) {
      logger.warn('Failed to extract roles from token:', error);
    }

    const allRoles = [...realmRoles, ...clientRoles];

    return {
      id: userData.sub,
      email: userData.email,
      name: userData.name || userData.preferred_username,
      username: userData.preferred_username,
      roles: allRoles,
      groups: userData.groups || [],
      picture: userData.picture,
    };
  }

  // ================== Private Methods ==================

  private async buildAuthUrl(options?: LoginOptions): Promise<string> {
    // Generate PKCE parameters
    const { codeVerifier, codeChallenge } = await this.generatePKCEParams();
    
    // Store code verifier in sessionStorage for later use
    sessionStorage.setItem(KeycloakProvider.CODE_VERIFIER_KEY, codeVerifier);

    const params = new URLSearchParams({
      client_id: this.config.clientId,
      response_type: 'code',
      scope: options?.scope || 'openid profile email',
      redirect_uri: this.config.redirectUri,
      state: this.generateRandomString(32),
      nonce: this.generateRandomString(32),
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });

    return `${this.config.url}/realms/${this.config.realm}/protocol/openid-connect/auth?${params.toString()}`;
  }

  private async exchangeCodeForTokens(code: string): Promise<AuthTokens> {
    const tokenUrl = `${this.config.url}/realms/${this.config.realm}/protocol/openid-connect/token`;

    // Retrieve code verifier from sessionStorage
    const codeVerifier = sessionStorage.getItem(KeycloakProvider.CODE_VERIFIER_KEY);
    if (!codeVerifier) {
      throw new Error('Code verifier not found in session storage');
    }

    const params = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: this.config.clientId,
      code,
      redirect_uri: this.config.redirectUri,
      code_verifier: codeVerifier,
    });

    try {
      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Clean up code verifier on error
        sessionStorage.removeItem(KeycloakProvider.CODE_VERIFIER_KEY);
        throw new Error(errorData.error_description || errorData.error || 'Token exchange failed');
      }

      const tokenData = await response.json();

      return this.parseTokenResponse(tokenData);
    } catch (error) {
      // Clean up code verifier on error
      sessionStorage.removeItem(KeycloakProvider.CODE_VERIFIER_KEY);
      throw error;
    }
  }

  private parseTokenResponse(tokenData: any): AuthTokens {
    const expiresAt = Date.now() + (tokenData.expires_in * 1000);

    return {
      accessToken: tokenData.access_token,
      refreshToken: tokenData.refresh_token,
      idToken: tokenData.id_token,
      tokenType: tokenData.token_type || 'Bearer',
      expiresAt,
      scope: tokenData.scope,
    };
  }

  private async generatePKCEParams(): Promise<{ codeVerifier: string; codeChallenge: string }> {
    const codeVerifier = this.generateCodeVerifier();
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);

    return { codeVerifier, codeChallenge };
  }

  private generateCodeVerifier(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return this.base64URLEncode(array);
  }

  private async generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return this.base64URLEncode(new Uint8Array(digest));
  }

  private base64URLEncode(array: Uint8Array): string {
    const base64 = btoa(String.fromCharCode(...array));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }

  private generateRandomString(length: number): string {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return this.base64URLEncode(array).substring(0, length);
  }
} 